import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useCan, useMe } from "@/lib/shell-context";
import {
  api,
  type MessageRow,
  type Page,
  type Player,
  type PlayerDetail,
  type Source,
} from "@/lib/api";
import {
  Button,
  Empty,
  ErrorNote,
  Input,
  Loading,
  Mono,
  Panel,
  PlayerNo,
  Select,
  Status,
  Table,
  Td,
  Th,
} from "@/components/primitives";

/**
 * Players (spec §12 Users).
 *
 * The #number leads every row. It is what CS reads off a Topic title, and the
 * only handle that exists on the Telegram side — the real name and username
 * live here and nowhere else (decision 6).
 */

function when(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  const days = (Date.now() - date.getTime()) / 86_400_000;
  if (days < 1) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (days < 7) return `${Math.floor(days)}d ago`;
  return date.toLocaleDateString();
}

export function PlayersPage() {
  const { botId } = useParams();
  const nameHidden = useMe().permissions.data.playerName === "hidden";
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [topicActive, setTopicActive] = useState("");
  const [page, setPage] = useState(1);

  const params = new URLSearchParams({ page: String(page) });
  if (search.trim()) params.set("q", search.trim());
  if (status) params.set("status", status);
  if (topicActive) params.set("topicActive", topicActive);

  const query = useQuery({
    queryKey: ["players", botId, params.toString()],
    queryFn: () => api.get<Page<Player>>(`/bots/${botId}/players?${params}`),
    placeholderData: keepPreviousData,
  });

  const sources = useQuery({
    queryKey: ["sources", botId],
    queryFn: () => api.get<Source[]>(`/bots/${botId}/sources`),
  });
  const sourceName = (id: number | null) =>
    id === null ? "—" : (sources.data?.find((s) => s.id === id)?.name ?? "—");

  const data = query.data;

  return (
    <div className="grid grid-cols-1 gap-3">
      <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap">
        <Input
          className="col-span-2 sm:max-w-xs"
          placeholder="Search by #number, name, username or Telegram ID"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Select
          className="w-full sm:w-auto"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="blocked">Blocked the bot</option>
          <option value="deactivated">Account deleted</option>
          <option value="banned">Banned</option>
        </Select>
        <Select
          className="w-full sm:w-auto"
          value={topicActive}
          onChange={(e) => {
            setTopicActive(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Any</option>
          <option value="true">Has a Topic</option>
          <option value="false">No Topic yet</option>
        </Select>
        {data && (
          <span className="col-span-2 text-muted-foreground sm:ml-auto">
            {data.total.toLocaleString()} {data.total === 1 ? "player" : "players"}
          </span>
        )}
      </div>

      {query.error && <ErrorNote error={query.error} />}

      <Panel>
        {query.isLoading && !data ? (
          <Loading />
        ) : data && data.rows.length === 0 ? (
          <Empty
            title="No players match this filter"
            hint="Players appear the first time they message the bot. A deep link brings them in with a source attached."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th className="w-24">Number</Th>
                <Th>Name</Th>
                <Th className="w-40 md:max-xl:hidden">First source</Th>
                <Th className="w-40">Latest source</Th>
                <Th className="w-32">Status</Th>
                <Th className="w-24 md:max-xl:hidden">Topic</Th>
                <Th className="w-28">Last seen</Th>
              </tr>
            </thead>
            <tbody>
              {data?.rows.map((player) => (
                <tr key={player.id} className="hover:bg-secondary/50">
                  <Td primary>
                    <Link to={`/bots/${botId}/players/${player.id}`}>
                      <PlayerNo value={player.playerNo} className="text-primary" />
                    </Link>
                  </Td>
                  <Td label="Name">
                    <span>
                      <Link
                        to={`/bots/${botId}/players/${player.id}`}
                        className="hover:underline underline-offset-2"
                      >
                        {[player.firstName, player.lastName].filter(Boolean).join(" ") ||
                          (nameHidden ? "Name hidden" : "Player")}
                      </Link>
                      {player.username && (
                        <Mono className="block max-w-56 truncate text-muted-foreground">
                          @{player.username}
                        </Mono>
                      )}
                    </span>
                  </Td>
                  <Td label="First source" className="text-muted-foreground md:max-xl:hidden">
                    <span
                      className="block max-w-44 truncate"
                      title={sourceName(player.firstSourceId)}
                    >
                      {sourceName(player.firstSourceId)}
                    </span>
                  </Td>
                  <Td label="Latest source">
                    <span
                      className="block max-w-44 truncate"
                      title={sourceName(player.latestSourceId)}
                    >
                      {sourceName(player.latestSourceId)}
                    </span>
                  </Td>
                  <Td label="Status">
                    <Status value={player.status} />
                  </Td>
                  <Td label="Topic" className="text-muted-foreground md:max-xl:hidden">
                    {player.topicActive ? "Open" : "—"}
                  </Td>
                  <Td label="Last seen" className="text-muted-foreground">
                    {when(player.lastSeenAt)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      {data && data.total > data.pageSize && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="quiet" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="text-muted-foreground">
            Page {data.page} of {Math.ceil(data.total / data.pageSize)}
          </span>
          <Button
            variant="quiet"
            size="sm"
            disabled={page >= Math.ceil(data.total / data.pageSize)}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export function PlayerDetailPage() {
  const { botId, playerId } = useParams();
  const me = useMe();
  const canBan = useCan("players", "ban");
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ["player", playerId],
    queryFn: () => api.get<PlayerDetail>(`/players/${playerId}`),
  });

  const messages = useQuery({
    queryKey: ["player-messages", playerId],
    queryFn: () => api.get<Page<MessageRow>>(`/players/${playerId}/messages`),
  });

  const ban = useMutation({
    mutationFn: (banned: boolean) => api.post(`/players/${playerId}/${banned ? "ban" : "unban"}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["player", playerId] });
    },
  });

  if (detail.isLoading) return <Loading />;
  if (detail.error) return <ErrorNote error={detail.error} />;

  const { player, topic, sourceHistory } = detail.data!;
  const name = [player.firstName, player.lastName].filter(Boolean).join(" ") || "Player";

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Link to={`/bots/${botId}/players`} className="text-muted-foreground hover:underline">
          Players
        </Link>
        <PlayerNo value={player.playerNo} />
        <h1 className="min-w-0 text-[15px] font-medium break-words">{name}</h1>
        <Status value={player.status} />
        {canBan && (
          <div className="ml-auto">
            {player.status === "banned" ? (
              <Button variant="quiet" onClick={() => ban.mutate(false)} disabled={ban.isPending}>
                Unban player
              </Button>
            ) : (
              <Button variant="danger" onClick={() => ban.mutate(true)} disabled={ban.isPending}>
                Ban player
              </Button>
            )}
          </div>
        )}
      </div>

      {ban.error && <ErrorNote error={ban.error} />}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_280px] lg:grid-cols-[minmax(0,1fr)_320px]">
        <Panel title={`Messages${messages.data?.redacted ? " — metadata only" : ""}`}>
          {messages.isLoading ? (
            <Loading />
          ) : messages.data && messages.data.rows.length === 0 ? (
            <Empty
              title="Nothing sent yet"
              hint="This player exists because they tapped a link or a button. A Topic is created the first time they type."
            />
          ) : (
            <ul className="divide-y divide-border/60">
              {messages.data?.rows.map((message) => (
                <li key={message.id} className="grid gap-1 px-5 py-2.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-muted-foreground">
                    <span>
                      {message.direction === "player_to_cs"
                        ? "Player"
                        : message.direction === "cs_to_player"
                          ? "CS"
                          : "Bot"}
                    </span>
                    <span>{new Date(message.createdAt).toLocaleString()}</span>
                    <Mono>{message.type}</Mono>
                    <Status value={message.status} />
                    {message.isRevoked && <span className="text-destructive">revoked</span>}
                  </div>
                  {message.text || message.caption ? (
                    <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">
                      {message.text ?? message.caption}
                    </p>
                  ) : null}
                  {message.lastError && (
                    <p className="text-destructive [overflow-wrap:anywhere]">{message.lastError}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <div className="grid content-start gap-4">
          <Panel title="Identity">
            <dl className="grid gap-2 px-5 py-3">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Telegram ID</dt>
                <dd>
                  {player.telegramUserId === null ? (
                    <span className="text-muted-foreground">Hidden</span>
                  ) : (
                    <Mono>{player.telegramUserId}</Mono>
                  )}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Username</dt>
                <dd>
                  {player.username ? (
                    <Mono>@{player.username}</Mono>
                  ) : me.permissions.data.username === "hidden" ? (
                    <span className="text-muted-foreground">Hidden</span>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Language</dt>
                <dd>{player.languageCode ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">First seen</dt>
                <dd>{new Date(player.firstSeenAt).toLocaleDateString()}</dd>
              </div>
            </dl>
            <p className="border-t border-border px-5 py-2.5 text-muted-foreground">
              None of this reaches the CS group. Agents see only the Topic name.
            </p>
          </Panel>

          <Panel title="Topic">
            {topic ? (
              <div className="grid gap-2 px-5 py-3">
                {/* Shown exactly as it reads in Telegram — the whole point of
                    the naming rule is what CS can and cannot see. */}
                <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 [overflow-wrap:anywhere]">
                  {topic.topicName}
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Thread</span>
                  <Mono>{topic.messageThreadId}</Mono>
                </div>
                {topic.previousThreadIds && topic.previousThreadIds.length > 0 && (
                  <div className="text-muted-foreground">
                    Recreated {topic.previousThreadIds.length}×, same number kept
                  </div>
                )}
              </div>
            ) : (
              <p className="px-5 py-3 text-muted-foreground">
                No Topic yet. One is created the first time this player types a message.
              </p>
            )}
          </Panel>

          <Panel title="Source history">
            {sourceHistory.length === 0 ? (
              <p className="px-5 py-3 text-muted-foreground">No attribution recorded.</p>
            ) : (
              <ul className="divide-y divide-border/60">
                {sourceHistory.map((event) => (
                  <li key={event.id} className="flex justify-between gap-3 px-5 py-2">
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      {event.sourceName ?? (
                        <span className="text-muted-foreground">
                          Unknown code <Mono>{event.rawCode}</Mono>
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-muted-foreground">
                      {new Date(event.createdAt).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
